import { GenericError } from '../../errors/shared/genericError.js';
import { UnknownError } from '../../errors/shared/base.js';
import { Err } from '../../result/shared/result.js';
import { isHTTPHeaders, HTTPHeaders } from '../shared/headers/Headers.js';
import { RequestContentDataType, contentTypeToHeader } from '../shared/headers/contentType.js';
import { FetchRequest } from './FetchRequest/FetchRequest.js';
import { Request } from './Request/Request.js';
import { urlToLog } from './url.js';
import { BASE_CLIENT_NAMESPACE } from './const.js';
import { HttpTransportType } from './types.js';
import { createLazyContextLogger } from '../../logger/browser/lazyLogger.js';

const defaultLogger = createLazyContextLogger(BASE_CLIENT_NAMESPACE);
/**
 * Создает и отправляет HTTP-запрос. Под капотом использует {@linkcode XMLHttpRequest}
 *
 * @example
 * ```js
 * const result = await sendHttpRequest(new URL('https://www.cian.ru/')); // HTTP GET
 *
 * if (result.isErr()) {
 *  ...
 * }
 *
 * const response = result.Ok;
 *
 * await response.compileBody();
 *
 * console.dir(response);
 * ```
 *
 * @export
 * @template Req - тип тела запроса
 * @template Res - тип тела ответа
 * @param {URL} url - URL запрашиваемого ресурса
 * @param {Partial<Omit<HTTPRequestParams<Req>, 'url'>>} [params={}] - конфигурация запроса
 * @return {(Promise<Result<IResponse<Res>, HttpTimeoutError | UnknownError>>)}
 */
async function sendHttpRequest(url, params = {}) {
    const { method = 'GET', headers: rawHeaders, requestServerName: originalRequestServerName, responseContentType, timeout, signal, body, transport: requestType, requestContentType, logger = defaultLogger, } = params;
    const requestServerName = originalRequestServerName || url.hostname;
    if (logger.debugEnabled) {
        logger.debug(`[${BASE_CLIENT_NAMESPACE}] call sendHttpRequest(url=${url.toString()})`);
    }
    let headers;
    if (rawHeaders && isHTTPHeaders(rawHeaders)) {
        headers = rawHeaders;
    }
    else {
        headers = new HTTPHeaders(rawHeaders);
    }
    if (requestContentType && body && !headers.get('content-type')) {
        if (requestContentType === RequestContentDataType.Blob) {
            headers.set('content-type', body.type);
        }
        else {
            const contentType = contentTypeToHeader(requestContentType);
            headers.set('content-type', contentType);
        }
    }
    const request = requestType === HttpTransportType.Fetch
        ? new FetchRequest({
            url,
            body,
            method,
            headers,
            requestServerName,
            responseContentType,
            timeout,
            signal,
        })
        : new Request({
            url,
            body,
            method,
            headers,
            requestServerName,
            responseContentType,
            timeout,
            signal,
        });
    if (signal?.aborted) {
        return GenericError.is(signal.reason)
            ? Err(signal.reason)
            : Err(new UnknownError({
                message: `Неизвестная причина отклонения запроса (не является GenericError) ${urlToLog(url)}`,
                domain: BASE_CLIENT_NAMESPACE + 'request.abort',
                details: signal.reason,
            }));
    }
    const responseResult = await request.send();
    if (responseResult.isErr()) {
        return responseResult;
    }
    return responseResult;
}

export { sendHttpRequest };
//# sourceMappingURL=client.js.map
